import { isReference } from '@apollo/client';
import type {
  FetchResult,
  InternalRefetchQueriesInclude,
  StoreObject,
} from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import { LegalDirectiveRecord } from '@models/legal-directive';

import { parseArgs, type CachedRecordList } from '@utils/apollo';

import {
  useListLegalDirectivesQuery,
  usePostLegalDirectiveRecordMutation,
  useUpdateLegalDirectiveRecordMutation,
  type PostLegalDirectiveRecordMutation,
  type PostLegalDirectiveRecordMutationVariables,
  type UpdateLegalDirectiveRecordMutation,
  type UpdateLegalDirectiveRecordMutationVariables,
} from 'generated/legal-directive.generated';

export const useGetLegalDirectives = (params?: { skip?: boolean }) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useListLegalDirectivesQuery({
    variables: {
      input: {
        patient: patient_id,
        count: 1500,
        page: 1,
      },
    },
    skip: !patient_id || params?.skip,
  });
  const { data, error } = response;
  handleError(error);
  const records = (data?.listLegalDirectives.data ?? []).map(
    (r) => new LegalDirectiveRecord(r)
  );
  return { ...response, data: records };
};

export function useCreateLegalDirective(): [
  (
    params: PostLegalDirectiveRecordMutationVariables
  ) => Promise<FetchResult<PostLegalDirectiveRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    usePostLegalDirectiveRecordMutation();
  const loading = baseLoading;

  async function createLegalDirective(
    params: PostLegalDirectiveRecordMutationVariables
  ) {
    const refetchQueries: InternalRefetchQueriesInclude = [];
    const consent = params.input;

    const response = await mutate({
      variables: { ...params },
      refetchQueries,
      update: (cache, { data: response }) => {
        if (!response?.postLegalDirectiveRecord) {
          return;
        }
        const storeObject =
          response.postLegalDirectiveRecord as unknown as StoreObject;
        try {
          cache.modify<{ listLegalDirectives: CachedRecordList }>({
            fields: {
              listLegalDirectives: (
                existing,
                { storeFieldName, toReference }
              ) => {
                if (isReference(existing)) {
                  return existing;
                }
                const parsed = parseArgs(storeFieldName);
                if (parsed?.patient === consent.patient?.reference) {
                  return {
                    ...existing,
                    data: [...existing.data, toReference(storeObject)],
                  };
                }
                return existing;
              },
            },
          });
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [createLegalDirective, loading];
}

export function useUpdateLegalDirective(): [
  (
    params: UpdateLegalDirectiveRecordMutationVariables
  ) => Promise<FetchResult<UpdateLegalDirectiveRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useUpdateLegalDirectiveRecordMutation();
  const loading = baseLoading;

  async function updateLegalDirective(
    params: UpdateLegalDirectiveRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.updateLegalDirectiveRecord) {
          return;
        }
        const { consent_id } = params.input;

        try {
          cache.modify({
            id: cache.identify({
              id: consent_id,
              __typename: 'ConsentModel',
            }),
            fields: {
              extension() {
                return response.updateLegalDirectiveRecord.extension;
              },
            },
          });
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [updateLegalDirective, loading];
}
